import * as THREE from "three";
import { MeshBVH } from "three-mesh-bvh";
import { applyMatrixToGeometry } from "./meshMath";
import { rigidIncrement, solveLinearSystem } from "./linalg";
import {
  faceNormal,
  sampleSurfaceAreaWeighted,
  type SurfaceSamples,
} from "./meshPrep";

export interface IcpResult {
  transform: THREE.Matrix4;
  /** RMS of inlier distances (mm). */
  rms: number;
  iterations: number;
  /** Share of source samples lying on the target within `inlierThresholdMm`. */
  inlierFraction: number;
  inlierRms: number;
  /** Which starting pose won the multi-start search. */
  start: string;
}

export interface IcpOptions {
  sampleCount?: number;
  maxIterations?: number;
  /** Upper bound on correspondence distance during the coarse search (mm). */
  maxCorrespondenceDistance?: number;
  inlierThresholdMm?: number;
  /**
   * Try all 24 axis-aligned orientations: `true` always, `false` never, unset only when
   * the near starts put almost no surface on the target.
   */
  allowGlobalSearch?: boolean;
  seed?: number;
  onProgress?: (iteration: number, rms: number) => void;
  onStage?: (message: string) => void;
}

interface RunConfig {
  iterations: number;
  maxCorrStart: number;
  minCorr: number;
  trim: number;
  normalDot: number;
}

interface RunResult {
  matrix: THREE.Matrix4;
  rms: number;
  iterations: number;
}

const scratch = {
  p: new THREE.Vector3(),
  n: new THREE.Vector3(),
  m: new THREE.Vector3(),
  rot: new THREE.Matrix3(),
  hit: { point: new THREE.Vector3(), distance: 0, faceIndex: 0 },
};

/** Trimmed point-to-plane ICP from `init`. */
function runIcp(
  samples: SurfaceSamples,
  bvh: MeshBVH,
  target: THREE.BufferGeometry,
  init: THREE.Matrix4,
  cfg: RunConfig,
  onIteration?: (iteration: number, rms: number) => void
): RunResult {
  const { p, n, m, rot, hit } = scratch;
  const matrix = init.clone();
  const count = samples.count;
  const px = new Float64Array(count * 3);
  const qx = new Float64Array(count * 3);
  const mx = new Float64Array(count * 3);
  const dist = new Float64Array(count);
  let maxCorr = cfg.maxCorrStart;
  let rms = Infinity;
  let iterations = 0;

  for (let iter = 0; iter < cfg.iterations; iter++) {
    iterations = iter + 1;
    rot.setFromMatrix4(matrix);
    let used = 0;
    for (let i = 0; i < count; i++) {
      p.set(samples.points[i * 3], samples.points[i * 3 + 1], samples.points[i * 3 + 2]).applyMatrix4(matrix);
      const found = bvh.closestPointToPoint(p, hit, 0, maxCorr);
      if (!found) continue;
      n.set(samples.normals[i * 3], samples.normals[i * 3 + 1], samples.normals[i * 3 + 2]).applyMatrix3(rot);
      faceNormal(target, hit.faceIndex, m);
      if (Math.abs(n.dot(m)) < cfg.normalDot) continue;
      px[used * 3] = p.x;
      px[used * 3 + 1] = p.y;
      px[used * 3 + 2] = p.z;
      qx[used * 3] = hit.point.x;
      qx[used * 3 + 1] = hit.point.y;
      qx[used * 3 + 2] = hit.point.z;
      mx[used * 3] = m.x;
      mx[used * 3 + 1] = m.y;
      mx[used * 3 + 2] = m.z;
      dist[used] = hit.distance;
      used++;
    }
    if (used < 12) break;

    const sorted = Float64Array.from(dist.subarray(0, used)).sort();
    const cutoff = sorted[Math.min(used - 1, Math.floor(used * cfg.trim))];
    const median = sorted[Math.floor(used / 2)];

    let cx = 0;
    let cy = 0;
    let cz = 0;
    let kept = 0;
    for (let i = 0; i < used; i++) {
      if (dist[i] > cutoff) continue;
      cx += px[i * 3];
      cy += px[i * 3 + 1];
      cz += px[i * 3 + 2];
      kept++;
    }
    if (kept < 12) break;
    cx /= kept;
    cy /= kept;
    cz /= kept;

    const A = new Float64Array(36);
    const b = new Float64Array(6);
    const J = new Float64Array(6);
    let sumSq = 0;
    for (let i = 0; i < used; i++) {
      if (dist[i] > cutoff) continue;
      const ax = px[i * 3] - cx;
      const ay = px[i * 3 + 1] - cy;
      const az = px[i * 3 + 2] - cz;
      const nx = mx[i * 3];
      const ny = mx[i * 3 + 1];
      const nz = mx[i * 3 + 2];
      const r =
        (px[i * 3] - qx[i * 3]) * nx +
        (px[i * 3 + 1] - qx[i * 3 + 1]) * ny +
        (px[i * 3 + 2] - qx[i * 3 + 2]) * nz;
      J[0] = ay * nz - az * ny;
      J[1] = az * nx - ax * nz;
      J[2] = ax * ny - ay * nx;
      J[3] = nx;
      J[4] = ny;
      J[5] = nz;
      for (let r0 = 0; r0 < 6; r0++) {
        b[r0] -= J[r0] * r;
        for (let c0 = r0; c0 < 6; c0++) A[r0 * 6 + c0] += J[r0] * J[c0];
      }
      sumSq += dist[i] * dist[i];
    }
    for (let r0 = 0; r0 < 6; r0++) {
      for (let c0 = 0; c0 < r0; c0++) A[r0 * 6 + c0] = A[c0 * 6 + r0];
    }
    let trace = 0;
    for (let k = 0; k < 6; k++) trace += A[k * 6 + k];
    for (let k = 0; k < 6; k++) A[k * 6 + k] += 1e-9 * trace + 1e-12;

    rms = Math.sqrt(sumSq / kept);
    onIteration?.(iterations, rms);

    const x = solveLinearSystem(A, b, 6);
    if (!x) break;
    const w = new THREE.Vector3(x[0], x[1], x[2]);
    const t = new THREE.Vector3(x[3], x[4], x[5]);
    const inc = rigidIncrement(w, t, new THREE.Vector3(cx, cy, cz));
    matrix.premultiply(inc);

    maxCorr = Math.min(cfg.maxCorrStart, Math.max(cfg.minCorr, median * 3));
    if (w.length() < 1e-7 && t.length() < 1e-6) break;
  }

  return { matrix, rms, iterations };
}

interface Evaluation {
  inliers: number;
  fraction: number;
  rms: number;
}

function evaluateAlignment(
  samples: SurfaceSamples,
  bvh: MeshBVH,
  target: THREE.BufferGeometry,
  matrix: THREE.Matrix4,
  threshold: number
): Evaluation {
  const { p, n, m, rot, hit } = scratch;
  rot.setFromMatrix4(matrix);
  let inliers = 0;
  let sumSq = 0;
  for (let i = 0; i < samples.count; i++) {
    p.set(samples.points[i * 3], samples.points[i * 3 + 1], samples.points[i * 3 + 2]).applyMatrix4(matrix);
    if (!bvh.closestPointToPoint(p, hit, 0, threshold)) continue;
    n.set(samples.normals[i * 3], samples.normals[i * 3 + 1], samples.normals[i * 3 + 2]).applyMatrix3(rot);
    faceNormal(target, hit.faceIndex, m);
    if (Math.abs(n.dot(m)) < 0.5) continue;
    inliers++;
    sumSq += hit.distance * hit.distance;
  }
  return {
    inliers,
    fraction: samples.count > 0 ? inliers / samples.count : 0,
    rms: inliers > 0 ? Math.sqrt(sumSq / inliers) : Infinity,
  };
}

/** The 24 proper rotations of the cube (axis permutations with signs, det = +1). */
function cubeRotations(): THREE.Matrix4[] {
  const perms = [
    [0, 1, 2],
    [0, 2, 1],
    [1, 0, 2],
    [1, 2, 0],
    [2, 0, 1],
    [2, 1, 0],
  ];
  const out: THREE.Matrix4[] = [];
  for (const perm of perms) {
    for (let signs = 0; signs < 8; signs++) {
      const rows = [0, 1, 2].map((r) => {
        const row = [0, 0, 0];
        row[perm[r]] = signs & (1 << r) ? -1 : 1;
        return row;
      });
      const det =
        rows[0][0] * (rows[1][1] * rows[2][2] - rows[1][2] * rows[2][1]) -
        rows[0][1] * (rows[1][0] * rows[2][2] - rows[1][2] * rows[2][0]) +
        rows[0][2] * (rows[1][0] * rows[2][1] - rows[1][1] * rows[2][0]);
      if (det < 0) continue;
      out.push(
        new THREE.Matrix4().set(
          rows[0][0], rows[0][1], rows[0][2], 0,
          rows[1][0], rows[1][1], rows[1][2], 0,
          rows[2][0], rows[2][1], rows[2][2], 0,
          0, 0, 0, 1
        )
      );
    }
  }
  return out;
}

function samplesCentroid(samples: SurfaceSamples): THREE.Vector3 {
  const c = new THREE.Vector3();
  for (let i = 0; i < samples.count; i++) {
    c.x += samples.points[i * 3];
    c.y += samples.points[i * 3 + 1];
    c.z += samples.points[i * 3 + 2];
  }
  return samples.count > 0 ? c.divideScalar(samples.count) : c;
}

const pause = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

/**
 * Register `sourceGeometry` onto the target surface (e.g. an arch onto its bite record).
 *
 * Several starting poses are tried (scanner frame as-is, centroid match, and if those
 * fail all 24 axis orientations); the one that puts the most source surface on the
 * target wins and is refined with trimmed point-to-plane ICP.
 */
export async function registerMeshToTarget(
  sourceGeometry: THREE.BufferGeometry,
  targetBvh: MeshBVH,
  targetGeometry: THREE.BufferGeometry,
  options: IcpOptions = {},
  apply = true
): Promise<IcpResult> {
  const seed = options.seed ?? 7;
  const threshold = options.inlierThresholdMm ?? 0.3;
  const coarse = sampleSurfaceAreaWeighted(sourceGeometry, 700, seed);
  const fine = sampleSurfaceAreaWeighted(
    sourceGeometry,
    options.sampleCount ?? 3000,
    seed + 1
  );

  if (!targetGeometry.boundingSphere) targetGeometry.computeBoundingSphere();
  const targetRadius = targetGeometry.boundingSphere!.radius;
  const targetCenter = sampleSurfaceCentroid(targetGeometry, seed + 2);
  const sourceCenter = samplesCentroid(fine);

  const coarseCfg: RunConfig = {
    iterations: 30,
    maxCorrStart: Math.min(
      options.maxCorrespondenceDistance ?? Infinity,
      Math.max(4, targetRadius * 0.25)
    ),
    minCorr: 0.4,
    trim: 0.8,
    normalDot: 0.3,
  };

  const centroidShift = new THREE.Matrix4().makeTranslation(
    targetCenter.x - sourceCenter.x,
    targetCenter.y - sourceCenter.y,
    targetCenter.z - sourceCenter.z
  );
  const starts: { label: string; matrix: THREE.Matrix4 }[] = [
    { label: "scanner frame", matrix: new THREE.Matrix4() },
    { label: "centroid match", matrix: centroidShift },
  ];

  interface Candidate {
    label: string;
    run: RunResult;
    evaluation: Evaluation;
  }
  const evaluateStart = (label: string, init: THREE.Matrix4): Candidate => {
    const run = runIcp(coarse, targetBvh, targetGeometry, init, coarseCfg);
    return {
      label,
      run,
      evaluation: evaluateAlignment(coarse, targetBvh, targetGeometry, run.matrix, threshold),
    };
  };
  const better = (a: Candidate, b: Candidate): Candidate =>
    a.evaluation.inliers > b.evaluation.inliers * 1.02 ||
    (a.evaluation.inliers >= b.evaluation.inliers * 0.98 && a.evaluation.rms < b.evaluation.rms)
      ? a
      : b;

  options.onStage?.(`Trying ${starts[0].label}…`);
  let winner = evaluateStart(starts[0].label, starts[0].matrix);
  for (const start of starts.slice(1)) {
    await pause();
    options.onStage?.(`Trying ${start.label}…`);
    winner = better(evaluateStart(start.label, start.matrix), winner);
  }

  const globalSearch =
    options.allowGlobalSearch ?? winner.evaluation.fraction < 0.12;
  if (globalSearch) {
    const rotations = cubeRotations();
    for (let r = 0; r < rotations.length; r++) {
      options.onStage?.(`Global orientation search ${r + 1}/${rotations.length}…`);
      const init = new THREE.Matrix4()
        .makeTranslation(targetCenter.x, targetCenter.y, targetCenter.z)
        .multiply(rotations[r])
        .multiply(new THREE.Matrix4().makeTranslation(-sourceCenter.x, -sourceCenter.y, -sourceCenter.z));
      winner = better(evaluateStart(`orientation ${r + 1}`, init), winner);
      if (r % 3 === 2) await pause();
    }
  }

  options.onStage?.(`Refining (${winner.label})…`);
  await pause();
  const refined = runIcp(
    fine,
    targetBvh,
    targetGeometry,
    winner.run.matrix,
    {
      iterations: options.maxIterations ?? 60,
      maxCorrStart: Math.max(0.8, Math.min(2.5, winner.evaluation.rms * 6)),
      minCorr: 0.12,
      trim: 0.9,
      normalDot: 0.5,
    },
    options.onProgress
  );

  const finalEval = evaluateAlignment(fine, targetBvh, targetGeometry, refined.matrix, threshold);
  if (apply) applyMatrixToGeometry(sourceGeometry, refined.matrix);

  return {
    transform: refined.matrix,
    rms: finalEval.rms,
    iterations: refined.iterations,
    inlierFraction: finalEval.fraction,
    inlierRms: finalEval.rms,
    start: winner.label,
  };
}

function sampleSurfaceCentroid(geometry: THREE.BufferGeometry, seed: number): THREE.Vector3 {
  return samplesCentroid(sampleSurfaceAreaWeighted(geometry, 4000, seed));
}

export function buildBvh(geometry: THREE.BufferGeometry): MeshBVH {
  if (!geometry.getAttribute("normal")) {
    geometry.computeVertexNormals();
  }
  return new MeshBVH(geometry);
}
